import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, ArrowLeft, Award, Zap } from 'lucide-react';
import PaymentForm from './PaymentForm';

/**
 * Pricing Page
 * Shows subscription options with Maxio payment integration
 */
const Pricing = () => {
  const navigate = useNavigate();
  const [showPaymentForm, setShowPaymentForm] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);

  const plans = [
    {
      name: 'Monthly',
      price: '$29',
      period: 'per month',
      description: 'Perfect for quick prep before MEPS',
      planId: 'monthly',
      productHandle: process.env.REACT_APP_MAXIO_PRODUCT_MONTHLY || 'startpatriot-monthly',
      features: [
        'Full access to 5,000+ practice questions',
        'All 10 ASVAB subtest sections',
        'Full-length practice tests',
        'AFQT score tracking',
        'Performance analytics',
        'Mobile-optimized study tools',
        'Cancel anytime'
      ],
      popular: false,
      color: 'from-blue-600 to-blue-800'
    },
    {
      name: '3-Month',
      price: '$69',
      period: 'one-time',
      description: 'Most popular for serious prep',
      planId: 'quarterly',
      productHandle: process.env.REACT_APP_MAXIO_PRODUCT_3MONTH || 'startpatriot-3month',
      savings: 'Save $18',
      features: [
        'Everything in Monthly plan',
        '3 months of unlimited access',
        'Best value for comprehensive prep',
        'Perfect for 6-12 week study plans',
        'Cancel anytime'
      ],
      popular: true,
      color: 'from-red-600 to-red-800'
    },
    {
      name: '6-Month',
      price: '$119',
      period: 'one-time',
      description: 'Maximum preparation time',
      planId: 'biannual',
      productHandle: process.env.REACT_APP_MAXIO_PRODUCT_6MONTH || 'startpatriot-6month',
      savings: 'Save $55',
      features: [
        'Everything in Monthly plan',
        '6 months of unlimited access',
        'Ideal for extensive preparation',
        'Take your time to master all sections',
        'Cancel anytime'
      ],
      popular: false,
      color: 'from-blue-700 to-blue-900'
    }
  ];

  const handleSelectPlan = (planId) => {
    console.log('=== OPENING PAYMENT FORM ===');
    console.log('Selected plan:', planId);

    // Find the selected plan
    const plan = plans.find(p => p.planId === planId);
    if (!plan) {
      console.error('Plan not found:', planId);
      return;
    }

    // Open payment form modal
    setSelectedPlan(plan);
    setShowPaymentForm(true);
  };

  return (
    <div
      className="min-h-screen relative"
      style={{
        backgroundImage: `url(${process.env.PUBLIC_URL}/soldier-flag-bg.png)`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
        backgroundAttachment: 'fixed'
      }}
    >
      {/* Overlay */}
      <div className="absolute inset-0 bg-white/20 pointer-events-none"></div>

      {/* Content */}
      <div className="relative z-10">
        {/* Navigation */}
        <nav className="bg-gradient-to-r from-blue-900 via-white to-red-700 border-b-4 border-blue-800 sticky top-0 z-50 shadow-xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 bg-white/95 backdrop-blur">
            <div className="flex justify-between items-center h-16">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 text-gray-800 hover:text-blue-800 transition font-semibold"
              >
                <ArrowLeft className="w-5 h-5" />
                Back to Home
              </button>
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 bg-gradient-to-br from-blue-700 via-white to-red-700 rounded-full flex items-center justify-center shadow-lg">
                  <Star className="w-5 h-5 text-blue-800 fill-blue-800" />
                </div>
                <span className="text-2xl font-black bg-gradient-to-r from-blue-900 via-red-700 to-blue-900 bg-clip-text text-transparent tracking-tight">StartPatriot</span>
              </div>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-blue-900/90 via-red-900/90 to-blue-900/90">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-red-600 text-white px-6 py-3 rounded-full text-sm font-bold mb-6 shadow-xl border-4 border-white">
              <Award className="w-5 h-5" />
              TRUSTED BY 12,500+ MILITARY RECRUITS
              <Award className="w-5 h-5" />
            </div>

            <h1 className="text-5xl md:text-6xl font-black text-white mb-6 uppercase tracking-tight drop-shadow-[0_4px_8px_rgba(0,0,0,0.8)]">
              Choose Your Plan
            </h1>
            <p className="text-2xl text-white font-bold max-w-3xl mx-auto drop-shadow-[0_2px_4px_rgba(0,0,0,0.8)]">
              Get full access to all ASVAB prep materials. No hidden fees. Cancel anytime.
            </p>
          </div>
        </section>

        {/* Pricing Cards */}
        <section className="py-20 bg-gradient-to-b from-white/95 to-blue-50/95">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {plans.map((plan, idx) => (
                <div
                  key={idx}
                  className={`relative bg-white rounded-2xl shadow-2xl border-4 ${
                    plan.popular ? 'border-yellow-400 scale-105' : 'border-blue-200'
                  } hover:scale-110 transition-transform duration-300`}
                >
                  {/* Popular Badge */}
                  {plan.popular && (
                    <div className="absolute -top-5 left-1/2 transform -translate-x-1/2">
                      <div className="bg-gradient-to-r from-yellow-400 to-yellow-600 text-black px-6 py-2 rounded-full font-black text-sm uppercase shadow-lg flex items-center gap-2 border-2 border-yellow-700">
                        <Zap className="w-4 h-4 fill-black" />
                        Most Popular
                      </div>
                    </div>
                  )}

                  <div className="p-8">
                    {/* Plan Header */}
                    <div className={`mb-6 pb-6 border-b-2 border-gray-200`}>
                      <h3 className="text-3xl font-black text-gray-800 mb-2 uppercase">{plan.name}</h3>
                      <p className="text-gray-600 font-semibold mb-4">{plan.description}</p>
                      <div className="flex items-baseline gap-2">
                        <span className="text-5xl font-black text-gray-900">{plan.price}</span>
                        <span className="text-gray-600 font-medium">{plan.period}</span>
                      </div>
                      {plan.savings && (
                        <div className="mt-3 inline-block bg-green-100 text-green-800 px-4 py-1 rounded-full text-sm font-black border-2 border-green-300">
                          {plan.savings}
                        </div>
                      )}
                    </div>

                    {/* Features */}
                    <ul className="space-y-4 mb-8">
                      {plan.features.map((feature, featureIdx) => (
                        <li key={featureIdx} className="flex items-start gap-3">
                          <Check className="w-6 h-6 text-green-600 flex-shrink-0 mt-0.5" />
                          <span className="text-gray-700 font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>

                    {/* CTA Button */}
                    <button
                      onClick={() => handleSelectPlan(plan.planId)}
                      className={`w-full px-6 py-4 bg-gradient-to-r ${plan.color} text-white text-xl font-black rounded-xl hover:shadow-2xl transition-all duration-300 border-2 border-gray-800 uppercase flex items-center justify-center gap-2`}
                    >
                      <Star className="w-5 h-5 fill-white" />
                      Get Started
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* FAQ Preview */}
            <div className="mt-16 text-center">
              <h2 className="text-3xl font-black text-gray-800 mb-6 uppercase">All Plans Include</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-200">
                  <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h3 className="font-black text-xl text-gray-800 mb-2">5,000+ Questions</h3>
                  <p className="text-gray-600 font-medium">Comprehensive coverage of all ASVAB topics</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-red-200">
                  <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h3 className="font-black text-xl text-gray-800 mb-2">Full-Length Tests</h3>
                  <p className="text-gray-600 font-medium">Realistic practice exams with instant scoring</p>
                </div>
                <div className="bg-white rounded-xl p-6 shadow-lg border-2 border-blue-200">
                  <Check className="w-12 h-12 text-green-600 mx-auto mb-3" />
                  <h3 className="font-black text-xl text-gray-800 mb-2">Progress Tracking</h3>
                  <p className="text-gray-600 font-medium">Monitor your AFQT score improvements</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-gradient-to-r from-blue-950/95 via-red-950/95 to-blue-950/95 text-gray-300 py-12 border-t-8 border-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-blue-200 font-bold uppercase tracking-wide">&copy; 2025 StartPatriot. All rights reserved.</p>
          </div>
        </footer>
      </div>

      {/* Payment Form Modal */}
      {selectedPlan && (
        <PaymentForm
          isOpen={showPaymentForm}
          onClose={() => setShowPaymentForm(false)}
          plan={selectedPlan}
        />
      )}
    </div>
  );
};

export default Pricing;
